import { useEffect, useMemo, useRef, useState } from 'react';
import apiService from '../services/apiService';
import {
    apiDetail, COPY, findNode, flattenOrgs, kpisFor, roleChoicesFor, searchUsers,
} from '../utils/adminConsole';

/**
 * The admin console's state and calls, shared by today's console (v1) and
 * the redesigned one (v2) so their behaviour cannot drift:
 *
 * - orgs + users load in parallel; a dealer-admin with one root lands in it;
 * - the level in view is a path of org ids (v1 drills it; v2 sets it as the
 *   Users "organization" scope); users always load with GET /users?org_id=,
 *   i.e. that org AND everything beneath it (subtree semantics);
 * - every write sets the row busy (double-click guard), clears the last
 *   error, then reloads the users of the level in view (no optimistic update);
 *   a failure shows the backend's `detail` text.
 *
 * Every action also returns a promise of true / false so v2 can toast.
 * `trackRoot` (v2): keep `rootUsers` (everyone in the caller's scope) fresh
 * too, for the Dealerships user counts and the access overview.
 */
export default function useAdminConsole({ me, trackRoot = false } = {}) {
    const [orgs, setOrgs] = useState([]);
    const [users, setUsers] = useState([]);
    const [rootUsers, setRootUsers] = useState([]);
    const [pathIds, setPathIds] = useState([]);
    const [query, setQuery] = useState('');
    const [loading, setLoading] = useState(true);
    // True from the start: the first load is a users load too (QA A-3).
    const [usersLoading, setUsersLoading] = useState(true);
    const [error, setError] = useState(null);
    const [actionError, setActionError] = useState(null);
    const [showAdd, setShowAdd] = useState(false);
    const [showOnboard, setShowOnboard] = useState(false);
    const [busyId, setBusyId] = useState(null);
    const [confirmId, setConfirmId] = useState(null);
    const [resetConfirm, setResetConfirm] = useState(null);
    const [resetResult, setResetResult] = useState(null);
    // org edit / delete / add-child
    const [editTarget, setEditTarget] = useState(null);
    const [deleteTarget, setDeleteTarget] = useState(null);
    const [addChild, setAddChild] = useState(null); // { type, label, parentId }
    const [addChildName, setAddChildName] = useState('');
    const [orgBusy, setOrgBusy] = useState(false);
    const [orgError, setOrgError] = useState(null);

    const isSuper = !!me?.is_super_admin;
    const canEdit = !!me?.is_admin;
    const roleChoices = roleChoicesFor(isSuper);

    const currentId = pathIds.length ? pathIds[pathIds.length - 1] : null;
    const currentNode = currentId ? findNode(orgs, currentId) : null;
    const childOrgs = currentNode ? (currentNode.children || []) : orgs;

    // Only the newest users request may land (a fast scope change must not
    // be overwritten by a slow earlier answer).
    const usersSeq = useRef(0);
    const loadUsers = (orgId) => {
        usersSeq.current += 1;
        const seq = usersSeq.current;
        setUsersLoading(true);
        return apiService.getAdminUsers(orgId)
            .then((u) => {
                if (seq !== usersSeq.current) return;
                setUsers(u);
                if (!orgId) setRootUsers(u);
            })
            .catch(() => { if (seq === usersSeq.current) setUsers([]); })
            .finally(() => { if (seq === usersSeq.current) setUsersLoading(false); });
    };
    const refreshRoot = () => apiService.getAdminUsers(null).then(setRootUsers).catch(() => {});
    const refreshOrgs = () => apiService.getAdminOrgs().then(setOrgs).catch(() => {});
    // After a write: the level in view, and (v2) everybody when scoped.
    const reloadAfterWrite = () => {
        loadUsers(currentId);
        if (trackRoot && currentId) refreshRoot();
    };

    useEffect(() => {
        let alive = true;
        Promise.all([apiService.getAdminOrgs(), apiService.getAdminUsers()])
            .then(([o, u]) => {
                if (!alive) return;
                setOrgs(o); setUsers(u); setRootUsers(u);
                // A dealer-admin has a single root — land them straight in it.
                if (!me?.is_super_admin && o.length === 1) { setPathIds([o[0].id]); loadUsers(o[0].id); }
            })
            .catch(() => { if (alive) setError(COPY.loadError); })
            .finally(() => {
                if (!alive) return;
                setLoading(false);
                // Unless a dealer-admin's own team load took over, the first load is done.
                if (usersSeq.current === 0) setUsersLoading(false);
            });
        return () => { alive = false; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const drill = (node) => { setPathIds((p) => [...p, node.id]); loadUsers(node.id); setQuery(''); };
    const goTo = (id) => {
        if (id === null) { setPathIds([]); loadUsers(null); return; }
        const idx = pathIds.indexOf(id);
        setPathIds(pathIds.slice(0, idx + 1)); loadUsers(id);
    };
    /** v2: the Users organization scope (one org, or everyone). */
    const setScope = (id) => {
        setPathIds(id ? [id] : []);
        loadUsers(id || null);
    };

    const patchUser = (u, payload) => {
        setBusyId(u.id); setActionError(null);
        return apiService.updateAdminUser(u.id, payload)
            .then(() => { reloadAfterWrite(); return true; })
            .catch((e) => { setActionError(apiDetail(e, COPY.updateFailed)); return false; })
            .finally(() => setBusyId(null));
    };
    /** Role presets: the admin's assignment for this member in `orgId` (null = system default). */
    const setMemberPreset = (u, orgId, presetId) => {
        setBusyId(u.id); setActionError(null);
        return apiService.setAdminMemberPreset(orgId, u.id, presetId || null)
            .then(() => { reloadAfterWrite(); return true; })
            .catch((e) => { setActionError(apiDetail(e, COPY.updateFailed)); return false; })
            .finally(() => setBusyId(null));
    };
    const doRemove = (u) => {
        setBusyId(u.id); setActionError(null);
        return apiService.removeAdminUser(u.id)
            .then(() => { setConfirmId(null); reloadAfterWrite(); return true; })
            .catch((e) => { setActionError(apiDetail(e, COPY.removeFailed)); return false; })
            .finally(() => setBusyId(null));
    };
    const doResetPw = (u) => {
        setBusyId(u.id); setActionError(null);
        return apiService.resetAdminUserPassword(u.id)
            .then((r) => { setResetConfirm(null); setResetResult(r); return true; })
            .catch((e) => { setActionError(apiDetail(e, COPY.resetFailed)); return false; })
            .finally(() => setBusyId(null));
    };

    // "Add dealership" under a holding: v1 at the holding level in view, v2 from the holding's row.
    const openAddChild = (parent = currentNode) => {
        if (!parent) return;
        setAddChild({ type: 'COG', label: '+ Add dealership', parentId: parent.id, parentName: parent.name });
        setAddChildName(''); setOrgError(null);
    };
    const doAddChild = () => {
        const name = addChildName.trim();
        const parentId = addChild?.parentId;
        if (!name || !parentId) return Promise.resolve(false);
        setOrgBusy(true); setOrgError(null);
        return apiService.createAdminOrg({ name, type: addChild.type, parent_id: parentId })
            .then(() => { setAddChild(null); setAddChildName(''); return refreshOrgs().then(() => true); })
            .catch((e) => { setOrgError(apiDetail(e, COPY.addFailed)); return false; })
            .finally(() => setOrgBusy(false));
    };
    const openDelete = (node) => { setDeleteTarget(node); setOrgError(null); };
    const doDelete = () => {
        const id = deleteTarget.id;
        setOrgBusy(true); setOrgError(null);
        return apiService.deleteAdminOrg(id)
            .then(() => {
                setDeleteTarget(null);
                if (pathIds.includes(id)) {
                    const np = pathIds.slice(0, pathIds.indexOf(id));
                    setPathIds(np);
                    loadUsers(np.length ? np[np.length - 1] : null);
                } else {
                    loadUsers(currentId);
                }
                if (trackRoot) refreshRoot();
                return refreshOrgs().then(() => true);
            })
            .catch((e) => { setOrgError(apiDetail(e, COPY.deleteFailed)); return false; })
            .finally(() => setOrgBusy(false));
    };

    // Results of the modals, reported back to the console.
    const onUserAdded = (res) => {
        reloadAfterWrite();
        if (res?.user) setResetResult({ email: res.user?.email, email_sent: res.email_sent });
    };
    const onOnboarded = (r) => {
        refreshOrgs(); reloadAfterWrite();
        if (r?.admin) setResetResult({ email: r.admin?.email, email_sent: r.admin_email_sent });
        else if (r?.invite_error) setActionError(r.invite_error);
    };
    const onOrgSaved = () => { refreshOrgs(); reloadAfterWrite(); };
    /** v2 Refresh: orgs and the users of the level in view. */
    const refreshAll = () => { refreshOrgs(); reloadAfterWrite(); };

    const crumbs = useMemo(() => {
        const out = [{ id: null, name: isSuper ? 'All organizations' : 'Your dealership' }];
        for (const id of pathIds) { const n = findNode(orgs, id); if (n) out.push({ id, name: n.name }); }
        return out;
    }, [pathIds, orgs, isSuper]);

    const kpis = useMemo(() => kpisFor(currentNode, orgs, users), [currentNode, orgs, users]);
    const childLabel = !currentNode ? 'Organizations' : currentNode.type === 'TOG' ? 'Dealerships' : 'Sub-organizations';
    const teamLabel = currentNode ? `${currentNode.name} · team` : (isSuper ? 'All users' : 'Team');
    // Level-aware "+ Add": a holding gets dealerships. Dealers are the leaf (no stores).
    const childAdd = currentNode?.type === 'TOG' && isSuper ? { type: 'COG', label: '+ Add dealership' } : null;
    // Flattened org list for moving a user between organizations.
    const orgOptions = useMemo(() => flattenOrgs(orgs), [orgs]);
    const filteredUsers = useMemo(() => searchUsers(users, query), [users, query]);

    return {
        // data
        orgs, users, rootUsers, loading, usersLoading, error, actionError, setActionError,
        // level in view
        pathIds, currentId, currentNode, childOrgs, crumbs, kpis, childLabel, teamLabel, childAdd,
        drill, goTo, setScope,
        // role
        isSuper, canEdit, roleChoices, orgOptions,
        // search
        query, setQuery, filteredUsers,
        // user actions
        busyId, patchUser, setMemberPreset, confirmId, setConfirmId, doRemove, resetConfirm, setResetConfirm, doResetPw,
        resetResult, setResetResult,
        // org actions
        editTarget, setEditTarget, deleteTarget, setDeleteTarget, openDelete, doDelete,
        addChild, setAddChild, openAddChild, addChildName, setAddChildName, doAddChild, orgBusy, orgError,
        // modals
        showAdd, setShowAdd, showOnboard, setShowOnboard,
        // reload
        loadUsers, reloadUsers: reloadAfterWrite, refreshOrgs, refreshRoot, refreshAll, onUserAdded, onOnboarded, onOrgSaved,
    };
}
